import * as Api from '@ViewModels';
import { Position, type Edge, type Node } from '@xyflow/react';
import { getResourceSelectorDisplayLabel, getTriggerTypeLabel } from '../../../../models/utils/automationDisplayConfig';
import { getPlainTextPreviewForRawRichTextContent } from '../../../../models/utils/richContentExtractionUtils';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import type { TriggerNodeData } from '../types/triggerNodes';
import { type BaseEdgeData, type BranchEdgeData, type PhantomAddPathEdgeData, EDGE_TYPES } from '../types/edges';
import { FLOW_VERTICAL_SPACING, SEGMENT_NODE, PHANTOM_EDGE } from './layoutConstants';
import { MAX_BRANCH_NESTING_DEPTH } from './nestingLimits';
import { BRANCH_COLORS, DEFAULT_BRANCH_COLOR } from '../edges/branchEdgeStyles';
import { applyLayout } from './layoutEngine';
import { createBehaviorCaseNode, createSegmentNode } from './nodeFactory';
import { getBehaviorCaseLabel } from './behaviorCaseLabels';
import { normalizeBehaviorBranchSchedule } from './behaviorBranchSchedule';
import {
	type BehaviorSwitchCaseLike,
	doesAutomationStepRequireSchedule,
	getBehaviorSwitchCaseCounts,
	hasPositiveTimeSpan,
	isBehaviorSwitchStepType,
	isBehaviorTimeoutCase,
} from './stepValidationUtils';

interface ConvertTemplateToFlowResult {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
}

interface ConvertContext {
	userSession?: Api.UserSessionContext;
	impersonationContext?: Api.IImpersonationContext;
	fetchedTemplates: Map<string, Api.IAutomationTemplate>;
	currentX: number;
	currentY: number;
	branchDepth: number;
	/**
	 * Root template used for shared rendering context (trigger, top-level permissions, etc.).
	 * Branch nodes still anchor schedule language to this root trigger.
	 */
	rootTemplate?: TemplateWithPermissions;
	/**
	 * The template that owns the steps being converted at the current recursion level.
	 * This is the template we must attach to step nodes for mutations and behavior analysis.
	 */
	template?: TemplateWithPermissions;
	/**
	 * The template ID that owns the steps being converted at the current recursion level.
	 * This is the template ID we must use for step mutations (save, schedule, etc.).
	 */
	ownerTemplateId: string;
	/** Which version (draft or published) we are expanding. */
	versionMode: 'draft' | 'published';
	visitedInCurrentPath: Set<string>;
	/** Stable path lineage used to generate canvas-wide branch path keys. */
	lineagePath: string[];
}

const DEFAULT_WAIT_TIMEOUT_DAYS = 3;

/**
 * Converts an automation template to React Flow nodes and edges.
 * Supports recursive expansion of switch steps if userSession is provided.
 *
 * @param template - The automation template to convert
 * @param options - Optional configuration for recursive expansion and layout
 */
/** Template with permission flags returned by the API but absent from the SDK type. */
type TemplateWithPermissions = Api.IAutomationTemplate & { canEdit?: boolean };

export async function convertTemplateToFlow(
	template: TemplateWithPermissions,
	options?: {
		userSession?: Api.UserSessionContext;
		impersonationContext?: Api.IImpersonationContext;
		expandBranches?: boolean;
	}
): Promise<ConvertTemplateToFlowResult> {
	if (!template) {
		return { nodes: [], edges: [] };
	}

	const version = template.draft || template.published;

	if (!version) {
		return { nodes: [], edges: [] };
	}

	// If expandBranches is enabled and we have userSession, use recursive expansion
	if (options?.expandBranches && options?.userSession) {
		const versionMode: ConvertContext['versionMode'] = template.draft ? 'draft' : 'published';

		const context: ConvertContext = {
			userSession: options.userSession,
			impersonationContext: options.impersonationContext,
			fetchedTemplates: new Map(),
			currentX: 400,
			currentY: 100,
			branchDepth: 0,
			rootTemplate: template,
			template,
			ownerTemplateId: template.id,
			versionMode,
			visitedInCurrentPath: new Set<string>(),
			lineagePath: [`template:${template.id}`],
		};

		const recursiveResult = await convertTemplateToFlowRecursive(version, context, null);

		// Apply dagre hierarchical layout
		return applyLayout(recursiveResult.nodes, recursiveResult.edges);
	}

	// Otherwise use simple linear conversion (backward compatible)
	const simpleResult = convertTemplateToFlowSimple(template, version, options?.impersonationContext);

	// Apply dagre hierarchical layout
	return applyLayout(simpleResult.nodes, simpleResult.edges);
}

/**
 * Simple linear conversion without branch expansion (original behavior)
 */
function convertTemplateToFlowSimple(
	template: TemplateWithPermissions,
	version: Api.IAutomationTemplateVersion,
	impersonationContext?: Api.IImpersonationContext
): ConvertTemplateToFlowResult {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge[] = [];

	const steps = version.steps || [];
	const trigger = version.trigger;

	let currentY = 100;
	const nodeSpacing = 200;
	const baseX = 400;

	// Always create a trigger node — manual automations have no trigger object (undefined/null)
	const triggerNodeData: TriggerNodeData = {
		label: trigger ? getTriggerLabel(trigger) : 'Manual',
		category: 'trigger',
		automationTrigger: trigger,
		automationTemplate: template,
		impersonationContext,
		isExpanded: false,
		isReadOnly: template.canEdit === false,
		metadata: {
			triggerType: trigger?._type || 'ManualAutomationTrigger',
		},
	};

	const triggerNode: Node<TriggerNodeData> = {
		id: 'trigger',
		type: NODE_TYPES.TRIGGER,
		position: { x: baseX, y: currentY },
		data: triggerNodeData,
		sourcePosition: Position.Bottom,
	};

	nodes.push(triggerNode as Node<BaseNodeData>);
	currentY += nodeSpacing;

	steps.forEach((step, index) => {
		const stepNode = convertStepToNodeSync(step, index, baseX, currentY, template);
		nodes.push(stepNode);

		const sourceId = index === 0 ? 'trigger' : `step-${steps[index - 1].id}`;
		const edge: Edge = {
			id: `${sourceId}-to-step-${step.id}`,
			source: sourceId,
			target: `step-${step.id}`,
			type: 'default',
		};
		edges.push(edge);

		currentY += nodeSpacing;
	});

	return { nodes, edges };
}

/**
 * Creates the trigger node for the automation flow
 */
function createTriggerNode(
	trigger: Api.IAutomationTrigger | undefined,
	context: ConvertContext,
	baseX: number,
	parentNodeId?: string
): { nodes: Node<BaseNodeData>[]; edges: Edge<BranchEdgeData | BaseEdgeData>[]; triggerId?: string; nextY: number } {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge<BranchEdgeData | BaseEdgeData>[] = [];
	let currentY = context.currentY;

	if (!parentNodeId) {
		// Always create a trigger node — manual automations have no trigger object (undefined/null)
		const triggerNodeData: TriggerNodeData = {
			label: trigger ? getTriggerLabel(trigger) : 'Manual',
			category: 'trigger',
			automationTrigger: trigger,
			automationTemplate: context.rootTemplate,
			impersonationContext: context.impersonationContext,
			isExpanded: false,
			isReadOnly: context.rootTemplate?.canEdit === false,
			metadata: {
				triggerType: trigger?._type || 'ManualAutomationTrigger',
			},
		};

		const triggerNode: Node<TriggerNodeData> = {
			id: 'trigger',
			type: NODE_TYPES.TRIGGER,
			position: { x: baseX, y: currentY },
			data: triggerNodeData,
			sourcePosition: Position.Bottom,
		};
		nodes.push(triggerNode as Node<BaseNodeData>);
		currentY += FLOW_VERTICAL_SPACING;
		return { nodes, edges, triggerId: 'trigger', nextY: currentY };
	}

	return { nodes, edges, triggerId: undefined, nextY: currentY };
}

function buildSegmentPathKey(
	context: ConvertContext,
	switchNodeId: string,
	caseId: string,
	caseIndex: number,
	kind: 'segment' | 'behaviorCase'
): string {
	const lineage = context.lineagePath.join('>');
	return `${lineage}>${kind}:${context.ownerTemplateId}:${switchNodeId}:${caseId}:${caseIndex}`;
}

/**
 * Walks the descendants of a switch branch (regular Switch segment OR BehaviorSwitch case)
 * and (a) propagates parentCaseInfo where it's not already set, and (b) normalizes the step
 * schedule against the root trigger's anchoring rule.
 *
 * The API publisher applies the same root-anchoring rule to children of both switch types
 * (AutomationTemplatePublisher.cs lines 456 & 514), so the FE must rewrite schedules
 * identically before they're displayed/saved.
 */
function applyBranchChildContext(
	nodes: Node<BaseNodeData>[],
	branchNodes: Node<BaseNodeData>[],
	parentCaseInfo: NonNullable<AutomationStepNodeData['parentCaseInfo']>,
	rootTrigger: Api.IAutomationTrigger | null | undefined
): void {
	for (const childNode of branchNodes) {
		const childIndex = nodes.findIndex(node => node.id === childNode.id);
		if (childIndex < 0) {
			continue;
		}

		const existingNode = nodes[childIndex];
		const existingParentCaseInfo = (existingNode.data as { parentCaseInfo?: unknown }).parentCaseInfo;
		const isBranchBoundaryNode =
			existingNode.hidden ||
			existingNode.type === NODE_TYPES.SEGMENT ||
			existingNode.type === NODE_TYPES.BEHAVIOR_CASE ||
			existingNode.type === NODE_TYPES.SWITCH_STEP ||
			existingNode.type === NODE_TYPES.TRIGGER ||
			existingNode.type === NODE_TYPES.PHANTOM_TARGET;

		if (isBranchBoundaryNode) {
			continue;
		}

		const existingNodeData = existingNode.data as {
			automationStep?: Api.IAutomationStep;
			stepData?: {
				schedule?: Api.IAutomationStepSchedule;
				stepType?: Api.AutomationStepType | 'TriggerAutomation';
			};
		};
		const sourceSchedule = existingNodeData.automationStep?.schedule ?? existingNodeData.stepData?.schedule;
		const normalizedSchedule =
			normalizeBehaviorBranchSchedule(sourceSchedule, rootTrigger, existingNodeData.stepData?.stepType) ?? undefined;

		// Capture the pre-normalization snapshot iff renormalization actually changed the criteria.
		// Downstream publish-validation reads this to detect that the persisted (server) record is
		// stale and surface a one-click refresh, even though the in-memory step is rewritten below.
		const persistedStep = existingNodeData.automationStep;
		// persistedStep is required to issue a corrective PUT, so reading persistedCriteria
		// from stepData.schedule when automationStep is absent would mark the node "stale"
		// with no way to repair it. Source from the same object as persistedStep.
		const persistedCriteria = persistedStep?.schedule?.criteria;
		const criteriaChanged =
			persistedStep != null &&
			normalizedSchedule != null &&
			persistedCriteria != null &&
			normalizedSchedule.criteria != null &&
			persistedCriteria !== normalizedSchedule.criteria;

		const nextNodeData: BaseNodeData & {
			automationStep?: Api.IAutomationStep;
			stepData?: { schedule?: Api.IAutomationStepSchedule };
			staleSchedule?: { persistedStep: Api.IAutomationStep; normalizedSchedule: Api.IAutomationStepSchedule };
		} = {
			...existingNode.data,
			parentCaseInfo: existingParentCaseInfo ?? parentCaseInfo,
		};

		if (criteriaChanged && persistedStep && normalizedSchedule) {
			nextNodeData.staleSchedule = { persistedStep, normalizedSchedule };
		}

		if (existingNodeData.automationStep) {
			nextNodeData.automationStep = {
				...existingNodeData.automationStep,
				schedule: normalizedSchedule,
			};
		}

		if (existingNodeData.stepData) {
			nextNodeData.stepData = {
				...existingNodeData.stepData,
				schedule: normalizedSchedule,
			};
		}

		nodes[childIndex] = {
			...existingNode,
			data: nextNodeData,
		};
	}
}

/**
 * Validates and fetches child template for a switch branch case
 * Returns the template version if valid, or creates error node and returns null
 */
async function validateAndFetchBranchTemplate(
	childTemplateId: string,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[],
	switchNodeId: string,
	caseId: string,
	caseIndex: number,
	caseStatement: Api.IAutomationStepCaseStatement,
	caseName: string,
	branchX: number,
	branchStartY: number
): Promise<{
	version: { steps?: Api.IAutomationStep[]; trigger?: Api.IAutomationTrigger };
	template: Api.IAutomationTemplate;
} | null> {
	if (context.visitedInCurrentPath.has(childTemplateId)) {
		console.error(`[convertTemplateToFlow] Circular reference detected: ${childTemplateId}. Skipping branch.`);
		return null;
	}

	if (context.branchDepth >= MAX_BRANCH_NESTING_DEPTH) {
		console.warn(
			`[convertTemplateToFlow] Maximum branch depth ${MAX_BRANCH_NESTING_DEPTH} reached at template ${childTemplateId}.`
		);
		return null;
	}

	const childTemplate = await fetchTemplateById(childTemplateId, context);

	if (!childTemplate) {
		createErrorNode(
			nodes,
			edges,
			switchNodeId,
			caseId,
			caseIndex,
			caseStatement,
			caseName,
			branchX,
			branchStartY,
			childTemplateId,
			'template_fetch_failed',
			'Failed to Load Template'
		);
		return null;
	}

	const childVersion =
		context.versionMode === 'draft' ? childTemplate.draft || childTemplate.published : childTemplate.published;

	if (!childVersion) {
		// Child template exists but has no version yet — normal for new/empty branches.
		// The segment node remains as the terminal node with an "Add Step" button.
		return null;
	}

	return { version: childVersion, template: childTemplate };
}

/**
 * Processes a single branch case within a switch step.
 * Creates a SegmentNode as the entry point, then connects to child steps.
 *
 * The visual source for branch edges is the parent step (not the hidden switch node)
 * to create a T-junction branching appearance.
 */
async function processSwitchBranchCase(
	caseStatement: Api.IAutomationStepCaseStatement,
	caseIndex: number,
	switchNodeId: string,
	visualSourceNodeId: string, // The node to visually branch from (parent step)
	branchX: number,
	branchStartY: number,
	totalCases: number,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): Promise<void> {
	const caseId = caseStatement.id || `case-${caseIndex}`;
	const childTemplateId = caseStatement.automationTemplateId;
	const caseName = caseStatement.isDefault ? 'Default' : caseStatement.name || `Case ${caseIndex + 1}`;
	const branchColor = caseStatement.isDefault ? DEFAULT_BRANCH_COLOR : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
	const segmentPathKey = buildSegmentPathKey(context, switchNodeId, caseId, caseIndex, 'segment');

	// Create SegmentNode as the entry point for this branch
	const segmentNode = createSegmentNode(
		caseStatement,
		caseIndex,
		switchNodeId,
		{ x: branchX, y: branchStartY },
		childTemplateId
	);
	segmentNode.data = {
		...segmentNode.data,
		segmentPathKey,
	};
	nodes.push(segmentNode as Node<BaseNodeData>);

	// Create branch edge from parent step to segment (T-junction visual style)
	// Uses visualSourceNodeId (parent step) instead of switchNodeId (hidden)
	const branchToSegmentEdge: Edge<BranchEdgeData> = {
		id: `${visualSourceNodeId}-to-${segmentNode.id}`,
		source: visualSourceNodeId, // Visual source is the parent step
		sourceHandle: 'default',
		target: segmentNode.id,
		type: EDGE_TYPES.BRANCH,
		data: {
			condition: caseStatement.filter,
			caseId,
			caseIndex,
			totalCases,
			isDefault: caseStatement.isDefault,
			caseName,
			caseColor: branchColor,
			switchNodeId, // Track the hidden switch node for data model
		},
	};
	edges.push(branchToSegmentEdge);

	// If there's a child template, process it and connect to the segment
	if (childTemplateId && context.userSession) {
		const fetchResult = await validateAndFetchBranchTemplate(
			childTemplateId,
			context,
			nodes,
			edges,
			switchNodeId,
			caseId,
			caseIndex,
			caseStatement,
			caseName,
			branchX,
			branchStartY + SEGMENT_NODE.MIN_HEIGHT + FLOW_VERTICAL_SPACING
		);

		if (fetchResult) {
			const { version: childVersion, template: childTemplate } = fetchResult;
			const newVisitedPath = new Set(context.visitedInCurrentPath);
			newVisitedPath.add(childTemplateId);

			const branchContext: ConvertContext = {
				...context,
				currentX: branchX,
				currentY: branchStartY + SEGMENT_NODE.MIN_HEIGHT + FLOW_VERTICAL_SPACING,
				branchDepth: context.branchDepth + 1,
				visitedInCurrentPath: newVisitedPath,
				lineagePath: [...context.lineagePath, `segment:${switchNodeId}:${caseId}`],
				// Keep the root template for trigger context, but attach the fetched child
				// template to branch nodes so step mutations and behavior analysis use the
				// correct branch-local steps.
				template: {
					...(childTemplate as TemplateWithPermissions),
					canEdit: (childTemplate as TemplateWithPermissions).canEdit ?? context.rootTemplate?.canEdit,
				},
				ownerTemplateId: childTemplateId,
			};

			const branchResult = await convertTemplateToFlowRecursive(childVersion, branchContext, segmentNode.id);

			nodes.push(...branchResult.nodes);
			edges.push(...branchResult.edges);

			// Connect segment to first child step
			if (branchResult.nodes.length > 0) {
				const firstChildNodeId = branchResult.nodes[0].id;
				const existingEdge = edges.some(edge => edge.source === segmentNode.id && edge.target === firstChildNodeId);

				if (!existingEdge) {
					const segmentToChildEdge: Edge<BaseEdgeData> = {
						id: `${segmentNode.id}-to-${firstChildNodeId}`,
						source: segmentNode.id,
						target: firstChildNodeId,
						type: EDGE_TYPES.DEFAULT,
					};
					edges.push(segmentToChildEdge);
				}

				// Propagate parentCaseInfo + normalize schedules across ALL descendants of this
				// segment branch (not just the first child). Regular Switch children are subject
				// to the same root-anchoring rule as behavior branches — see API publisher line 514.
				const segmentParentCaseInfo = {
					caseIndex,
					caseName,
					isDefault: caseStatement.isDefault || false,
					caseColor: branchColor,
					parentSwitchNodeId: switchNodeId,
					caseId,
					segmentPathKey,
					isBehaviorCase: false,
				};
				const rootTrigger = context.rootTemplate?.draft?.trigger || context.rootTemplate?.published?.trigger;
				applyBranchChildContext(nodes, branchResult.nodes, segmentParentCaseInfo, rootTrigger);
			}
		}
	}
}

/**
 * Processes a switch/conditional automation step and expands its branches.
 *
 * The switch node is kept hidden in the data model (for API compatibility) but
 * not rendered visually. Segments branch directly from the parent step using
 * T-junction style edge routing.
 */
async function processSwitchStep(
	step: Api.ISwitchAutomationStep,
	stepIndex: number,
	parentNodeId: string,
	previousStepId: string | undefined,
	baseX: number,
	currentY: number,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): Promise<{ maxY: number }> {
	const switchNodeId = `step-${step.id}`;
	const rawCases = step.cases || [];

	// Sort cases: non-default cases first (in original order), default case last
	// This ensures handle positions match lane positions (left-to-right: cases, then default)
	const cases = [...rawCases].sort((a, b) => {
		if (a.isDefault && !b.isDefault) return 1; // Default goes after non-default
		if (!a.isDefault && b.isDefault) return -1; // Non-default goes before default
		return 0; // Preserve original order for cases of same type
	});

	// Determine the parent step (the step BEFORE the switch) for visual branching.
	// IMPORTANT: use the `previousStepId` from the currently processed version's steps
	// array. Using context.template.{draft|published}.steps can be wrong for branch
	// templates (which may have both draft and published versions) and can cause edges
	// to originate from the wrong node.
	const previousNodeId = stepIndex === 0 || !previousStepId ? parentNodeId : `step-${previousStepId}`;

	// Create switch node but mark it as HIDDEN - segments branch from parent step visually
	const templateForNode = context.template
		? ({ ...context.template, id: context.ownerTemplateId } as TemplateWithPermissions)
		: undefined;
	const switchNode = convertStepToNodeSync(step, stepIndex, baseX, currentY, templateForNode);
	switchNode.hidden = true; // Hide the switch node visually
	switchNode.data.outputHandles = ['default'];
	switchNode.data.summary = cases.length > 0 ? `${cases.length} branches` : 'No branches';
	nodes.push(switchNode);

	// Segments start directly below the parent step (no visible switch in between)
	// Use a smaller gap since there's no switch node to account for
	const branchStartY = currentY + FLOW_VERTICAL_SPACING;
	let maxY = branchStartY;

	if (cases.length > 0) {
		const branchCount = cases.length;

		for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
			const caseStatement = cases[caseIndex];

			// Pass previousNodeId as the visual source for branch edges (not switchNodeId)
			await processSwitchBranchCase(
				caseStatement,
				caseIndex,
				switchNodeId, // Still track the switch for data model
				previousNodeId, // Visual source for branch edges
				baseX,
				branchStartY,
				branchCount,
				context,
				nodes,
				edges
			);
		}

		// Calculate the maximum extent of branches
		const maxBranchY = Math.max(
			...nodes.filter(n => n.position.y >= branchStartY).map(n => n.position.y),
			branchStartY
		);
		maxY = maxBranchY + FLOW_VERTICAL_SPACING;

		// Create phantom "Add Path" edge to the right of the default segment
		const phantomTargetX = baseX + branchCount * (SEGMENT_NODE.WIDTH + PHANTOM_EDGE.OFFSET_FROM_DEFAULT);

		// Create a phantom target node for edge positioning
		// Note: NOT hidden - React Flow needs to calculate edge endpoints
		// The PhantomTargetNode component renders as 1x1 invisible pixel
		const phantomTargetId = `phantom-target-${switchNodeId}`;
		const phantomTargetNode: Node<BaseNodeData> = {
			id: phantomTargetId,
			type: NODE_TYPES.PHANTOM_TARGET,
			position: { x: phantomTargetX, y: branchStartY },
			width: 1,
			height: 1,
			data: {
				label: '',
				category: 'action',
			},
		};
		nodes.push(phantomTargetNode);

		// Create phantom edge from parent step (not hidden switch) for T-junction visual
		const phantomEdgeData: PhantomAddPathEdgeData = {
			switchNodeId,
			segmentCount: branchCount,
		};

		const phantomEdge: Edge<PhantomAddPathEdgeData> = {
			id: `phantom-add-path-${switchNodeId}`,
			source: previousNodeId, // Branch from parent step visually
			sourceHandle: 'default',
			target: phantomTargetId,
			type: EDGE_TYPES.PHANTOM_ADD_PATH,
			data: phantomEdgeData,
		};
		edges.push(phantomEdge);
	}

	return { maxY };
}

function isBehaviorSwitchStep(step: Api.IAutomationStep): boolean {
	return isBehaviorSwitchStepType(step._type);
}

function getBehaviorCaseDisplayName(
	caseStatement: Api.IAutomationStepCaseStatement & {
		_type?: string;
		caseType?: string;
		engagementType?: string;
		isDefault?: boolean;
	},
	isDefaultCase: boolean,
	caseIndex: number
): string {
	const label = getBehaviorCaseLabel({
		...caseStatement,
		_type: (caseStatement._type || caseStatement.caseType || (isDefaultCase ? 'TimeoutCaseStatement' : undefined)) as
			| string
			| undefined,
		isDefault: isDefaultCase,
	});

	if (label === 'Behavior case') {
		return `Behavior Case ${caseIndex + 1}`;
	}

	return label;
}

function parseWaitTimeoutDays(waitTimeout: unknown): number {
	if (typeof waitTimeout === 'number' && Number.isFinite(waitTimeout)) {
		return Math.max(1, Math.round(waitTimeout));
	}

	if (typeof waitTimeout !== 'string') {
		return DEFAULT_WAIT_TIMEOUT_DAYS;
	}

	const trimmed = waitTimeout.trim();
	if (!trimmed) {
		return DEFAULT_WAIT_TIMEOUT_DAYS;
	}

	const dayMatch = trimmed.match(/^(\d+)\./);
	if (dayMatch) {
		return Math.max(1, Number(dayMatch[1]));
	}

	return DEFAULT_WAIT_TIMEOUT_DAYS;
}

async function processBehaviorSwitchStep(
	step: Api.IAutomationStep & {
		cases?: (Api.IAutomationStepCaseStatement & {
			_type?: string;
			caseType?: string;
			engagementType?: string;
			isTimeout?: boolean;
		})[];
		sourceStepId?: string;
		waitTimeout?: string;
	},
	stepIndex: number,
	parentNodeId: string,
	previousStepId: string | undefined,
	baseX: number,
	currentY: number,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): Promise<{ maxY: number; ownsContinuation: boolean }> {
	const behaviorSwitchNodeId = `step-${step.id}`;
	const rawCases = step.cases || [];

	const cases = [...rawCases].sort((a, b) => {
		const aIsDefault = isBehaviorTimeoutCase(a);
		const bIsDefault = isBehaviorTimeoutCase(b);
		if (aIsDefault && !bIsDefault) return 1;
		if (!aIsDefault && bIsDefault) return -1;
		return 0;
	});

	const previousNodeId = stepIndex === 0 || !previousStepId ? parentNodeId : `step-${previousStepId}`;
	const visualSourceNodeId = step.sourceStepId ? `step-${step.sourceStepId}` : previousNodeId;
	const visualSourceExists = nodes.some(node => node.id === visualSourceNodeId);
	const edgeSourceNodeId = visualSourceExists ? visualSourceNodeId : previousNodeId;
	const sourceStepTitle = (() => {
		if (!step.sourceStepId) {
			return undefined;
		}

		const sourceNode = nodes.find(node => node.id === `step-${step.sourceStepId}`);
		if (!sourceNode) {
			return undefined;
		}

		const sourceNodeData = sourceNode.data as {
			stepData?: { name?: unknown };
			label?: unknown;
		};
		const sourceName =
			typeof sourceNodeData.stepData?.name === 'string' && sourceNodeData.stepData.name.trim().length > 0
				? sourceNodeData.stepData.name.trim()
				: typeof sourceNodeData.label === 'string' && sourceNodeData.label.trim().length > 0
					? sourceNodeData.label.trim()
					: undefined;
		return sourceName;
	})();

	const templateForNode = context.template
		? ({ ...context.template, id: context.ownerTemplateId } as TemplateWithPermissions)
		: undefined;
	const switchNode = convertStepToNodeSync(step, stepIndex, baseX, currentY, templateForNode);
	switchNode.hidden = true;
	switchNode.data.outputHandles = ['default'];
	switchNode.data.summary = cases.length > 0 ? `${cases.length} behavior paths` : 'No behavior paths';
	nodes.push(switchNode);

	const branchStartY = currentY + FLOW_VERTICAL_SPACING;
	let maxY = branchStartY;

	if (cases.length > 0) {
		const branchCount = cases.length;

		for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
			const caseStatement = cases[caseIndex];
			const caseId = caseStatement.id || `case-${caseIndex}`;
			const childTemplateId = caseStatement.automationTemplateId;
			const isDefaultCase = isBehaviorTimeoutCase(caseStatement);
			const caseName = getBehaviorCaseDisplayName(caseStatement, isDefaultCase, caseIndex);
			const branchColor = isDefaultCase ? DEFAULT_BRANCH_COLOR : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
			const waitTimeoutDays = parseWaitTimeoutDays(step.waitTimeout);
			const segmentPathKey = buildSegmentPathKey(context, behaviorSwitchNodeId, caseId, caseIndex, 'behaviorCase');

			const behaviorCaseNode = createBehaviorCaseNode(
				{
					...caseStatement,
					name: caseName,
					waitTimeoutDays,
					sourceStepName: sourceStepTitle,
				},
				caseIndex,
				behaviorSwitchNodeId,
				{ x: baseX, y: branchStartY },
				childTemplateId
			);
			behaviorCaseNode.data = {
				...behaviorCaseNode.data,
				segmentPathKey,
			};
			nodes.push(behaviorCaseNode as Node<BaseNodeData>);

			edges.push({
				id: `${edgeSourceNodeId}-to-${behaviorCaseNode.id}`,
				source: edgeSourceNodeId,
				sourceHandle: 'default',
				target: behaviorCaseNode.id,
				type: EDGE_TYPES.BRANCH,
				data: {
					condition: caseStatement.filter,
					caseId,
					caseIndex,
					totalCases: branchCount,
					isDefault: !!isDefaultCase,
					caseName,
					caseColor: branchColor,
					switchNodeId: behaviorSwitchNodeId,
				},
			});

			if (childTemplateId && context.userSession) {
				const fetchResult = await validateAndFetchBranchTemplate(
					childTemplateId,
					context,
					nodes,
					edges,
					behaviorSwitchNodeId,
					caseId,
					caseIndex,
					caseStatement,
					caseName,
					baseX,
					branchStartY + FLOW_VERTICAL_SPACING
				);

				if (fetchResult) {
					const { version: childVersion, template: childTemplate } = fetchResult;
					const newVisitedPath = new Set(context.visitedInCurrentPath);
					newVisitedPath.add(childTemplateId);

					const branchContext: ConvertContext = {
						...context,
						currentX: baseX,
						currentY: branchStartY + FLOW_VERTICAL_SPACING,
						branchDepth: context.branchDepth + 1,
						visitedInCurrentPath: newVisitedPath,
						lineagePath: [...context.lineagePath, `behaviorCase:${behaviorSwitchNodeId}:${caseId}`],
						template: {
							...(childTemplate as TemplateWithPermissions),
							canEdit: (childTemplate as TemplateWithPermissions).canEdit ?? context.rootTemplate?.canEdit,
						},
						ownerTemplateId: childTemplateId,
					};

					const branchResult = await convertTemplateToFlowRecursive(childVersion, branchContext, behaviorCaseNode.id);

					nodes.push(...branchResult.nodes);
					edges.push(...branchResult.edges);

					if (branchResult.nodes.length > 0) {
						const firstChildNodeId = branchResult.nodes[0].id;
						const existingEdge = edges.some(
							edge => edge.source === behaviorCaseNode.id && edge.target === firstChildNodeId
						);

						if (!existingEdge) {
							edges.push({
								id: `${behaviorCaseNode.id}-to-${firstChildNodeId}`,
								source: behaviorCaseNode.id,
								target: firstChildNodeId,
								type: EDGE_TYPES.DEFAULT,
							});
						}

						// Propagate behavior context + normalize schedules across all descendants.
						const behaviorParentCaseInfo = {
							caseIndex,
							caseName,
							isDefault: !!isDefaultCase,
							caseColor: branchColor,
							parentSwitchNodeId: behaviorSwitchNodeId,
							caseId,
							segmentPathKey,
							isBehaviorCase: true,
							sourceStepName: sourceStepTitle,
						};
						const rootTrigger = context.rootTemplate?.draft?.trigger || context.rootTemplate?.published?.trigger;
						applyBranchChildContext(nodes, branchResult.nodes, behaviorParentCaseInfo, rootTrigger);
					}
				}
			}
		}

		const maxBranchY = Math.max(
			...nodes.filter(node => node.position.y >= branchStartY).map(node => node.position.y),
			branchStartY
		);
		maxY = maxBranchY + FLOW_VERTICAL_SPACING;

		// Intentionally no phantom "Add Path" edge for behavior switches.
		// Behavior branches are fully controlled via the email/text conditional
		// checkboxes and timeout settings, not by inline path insertion.
	}

	return { maxY, ownsContinuation: cases.length > 0 };
}

/**
 * Creates an error node for failed template loading
 */
function createErrorNode(
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[],
	switchNodeId: string,
	caseId: string,
	caseIndex: number,
	caseStatement: Api.IAutomationStepCaseStatement,
	caseName: string,
	x: number,
	y: number,
	templateId: string,
	errorType: string,
	errorLabel: string
): void {
	const errorNodeId = `error-${errorType}-${templateId}-${caseIndex}`;
	const errorNode: Node<BaseNodeData> = {
		id: errorNodeId,
		type: NODE_TYPES.ERROR,
		position: { x, y },
		data: {
			label: errorLabel,
			summary: `Template ID: ${templateId}`,
			category: 'action',
			metadata: {
				error: errorType,
				templateId,
			},
		},
		sourcePosition: Position.Bottom,
		targetPosition: Position.Top,
	};

	nodes.push(errorNode);

	edges.push({
		id: `${switchNodeId}-case-${caseId}-to-${errorNodeId}`,
		source: switchNodeId,
		sourceHandle: 'default', // Single centered source for all branches
		target: errorNodeId,
		type: 'branch',
		label: caseName,
		data: {
			condition: caseStatement.filter,
			caseId,
			caseIndex,
			isDefault: caseStatement.isDefault,
			caseName,
		},
	});
}

/**
 * Processes a regular (non-switch) automation step
 */
async function processRegularStep(
	step: Api.IAutomationStep,
	stepIndex: number,
	parentNodeId: string,
	previousStepId: string | undefined,
	baseX: number,
	currentY: number,
	context: ConvertContext
): Promise<{ node: Node<BaseNodeData>; edge: Edge; nextY: number }> {
	const templateForNode = context.template
		? ({ ...context.template, id: context.ownerTemplateId } as TemplateWithPermissions)
		: undefined;
	const stepNode = convertStepToNodeSync(step, stepIndex, baseX, currentY, templateForNode);

	const sourceId = stepIndex === 0 ? parentNodeId : `step-${previousStepId}`;
	const edge: Edge = {
		id: `${sourceId}-to-step-${step.id}`,
		source: sourceId,
		target: `step-${step.id}`,
		type: 'default',
	};

	return {
		node: stepNode,
		edge,
		nextY: currentY + FLOW_VERTICAL_SPACING,
	};
}

/**
 * Recursively converts template to flow with branch expansion
 */
async function convertTemplateToFlowRecursive(
	version: { steps?: Api.IAutomationStep[]; trigger?: Api.IAutomationTrigger },
	context: ConvertContext,
	parentNodeId: string | null
): Promise<ConvertTemplateToFlowResult> {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge[] = [];

	const steps = version.steps || [];
	const trigger = version.trigger;

	// Use context.currentX for all cases; final branch positioning is handled
	// by applySwitchBranchLayout() in layoutEngine.ts after Dagre runs
	const baseX = context.currentX;
	let currentY = context.currentY;

	const triggerResult = createTriggerNode(trigger, context, baseX, parentNodeId || undefined);
	nodes.push(...triggerResult.nodes);
	edges.push(...triggerResult.edges);
	currentY = triggerResult.nextY;

	const effectiveParentId = parentNodeId || triggerResult.triggerId || 'trigger';

	for (let i = 0; i < steps.length; i++) {
		const step = steps[i];
		const previousStepId = i > 0 ? steps[i - 1].id : undefined;

		if (step._type === 'SwitchAutomationStep') {
			const switchStep = step as Api.ISwitchAutomationStep;
			const switchResult = await processSwitchStep(
				switchStep,
				i,
				effectiveParentId,
				previousStepId,
				baseX,
				currentY,
				context,
				nodes,
				edges
			);
			currentY = switchResult.maxY;
		} else if (isBehaviorSwitchStep(step)) {
			const behaviorSwitchResult = await processBehaviorSwitchStep(
				step as Api.IAutomationStep & {
					cases?: (Api.IAutomationStepCaseStatement & {
						caseType?: string;
						engagementType?: string;
						waitTimeoutDays?: number;
						isTimeout?: boolean;
					})[];
					sourceStepId?: string;
				},
				i,
				effectiveParentId,
				previousStepId,
				baseX,
				currentY,
				context,
				nodes,
				edges
			);
			currentY = behaviorSwitchResult.maxY;

			// Behavior switches own continuation through their case branches.
			// Any linear steps that appear after the behavior switch in the same template
			// are unreachable and should not be rendered.
			if (behaviorSwitchResult.ownsContinuation) {
				break;
			}
		} else {
			const regularResult = await processRegularStep(
				step,
				i,
				effectiveParentId,
				previousStepId,
				baseX,
				currentY,
				context
			);
			nodes.push(regularResult.node);
			edges.push(regularResult.edge);
			currentY = regularResult.nextY;
		}
	}

	return { nodes, edges };
}

/**
 * Fetches a template by ID with caching
 */
async function fetchTemplateById(templateId: string, context: ConvertContext): Promise<Api.IAutomationTemplate | null> {
	// Check cache first
	if (context.fetchedTemplates.has(templateId)) {
		return context.fetchedTemplates.get(templateId)!;
	}

	if (!context.userSession) {
		return null;
	}

	try {
		const template = (await context.userSession.webServiceHelper.callAsync(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}`,
				impersonationContext: context.impersonationContext,
			}),
			'GET'
		)) as Api.IAutomationTemplate;

		context.fetchedTemplates.set(templateId, template);
		return template;
	} catch (error) {
		console.error(`[fetchTemplateById] Failed to fetch template ${templateId}:`, error);
		return null;
	}
}

/**
 * Synchronous step to node conversion (original implementation)
 */
function convertStepToNodeSync(
	step: Api.IAutomationStep,
	index: number,
	x: number,
	y: number,
	template?: Api.IAutomationTemplate
): Node<BaseNodeData> {
	const nodeType = getNodeTypeForStep(step._type);
	const mappedStepType = Api.apiModelTypeToAutomationStepType(step._type as Api.ApiModelType);
	const modelType = step._type as unknown as string;
	const customStepType =
		modelType === 'TriggerAutomationStep'
			? ('TriggerAutomation' as Api.AutomationStepType)
			: modelType === 'BehaviorSwitchAutomationStep'
				? Api.AutomationStepType.Switch
				: null;
	const stepTypeEnum = mappedStepType || customStepType;

	// Ensure we have a valid stepType enum value - keep error log for critical issues
	if (!stepTypeEnum) {
		console.error('[convertStepToNodeSync] Failed to convert step type:', {
			stepId: step.id,
			stepType: step._type,
			mappedNodeType: nodeType,
			stepTypeEnum,
		});
	}

	// Extract schedule with proper type guard
	const schedule: Api.IAutomationStepSchedule | undefined =
		'schedule' in step && step.schedule !== null && typeof step.schedule === 'object'
			? (step.schedule as Api.IAutomationStepSchedule)
			: undefined;

	const outputHandles = modelType === 'TriggerAutomationStep' ? [] : ['default'];

	const stepData: AutomationStepNodeData = {
		nodeId: `step-${step.id}`,
		stepId: step.id,
		stepType: stepTypeEnum || Api.AutomationStepType.Email, // Fallback to Email if conversion fails
		label: getStepLabel(step, index),
		category: 'action',
		automationStep: step,
		automationTemplate: template,
		isExpanded: false,
		isCompact: true,
		isValid: validateStep(step),
		validationErrors: [],
		stepData: {
			name: getStepNodeName(step),
			schedule,
			hasContent: checkStepHasContent(step),
		},
		outputHandles,
		metadata: {
			stepType: step._type,
		},
	};

	const node: Node<BaseNodeData> = {
		id: `step-${step.id}`,
		type: nodeType,
		position: { x, y },
		data: stepData,
		sourcePosition: Position.Bottom,
		targetPosition: Position.Top,
	};

	return node;
}

const STEP_TYPE_TO_NODE_TYPE: Record<string, string> = {
	EmailAutomationStep: NODE_TYPES.EMAIL_STEP,
	TextingAutomationStep: NODE_TYPES.TEXTING_STEP,
	ActionItemAutomationStep: NODE_TYPES.ACTION_ITEM_STEP,
	AddTagAutomationStep: NODE_TYPES.ADD_TAG_STEP,
	RemoveTagAutomationStep: NODE_TYPES.REMOVE_TAG_STEP,
	HandwrittenCardAutomationStep: NODE_TYPES.HANDWRITTEN_CARD_STEP,
	NoActionAutomationStep: NODE_TYPES.NO_ACTION_STEP,
	OpportunityAutomationStep: NODE_TYPES.OPPORTUNITY_STEP,
	HtmlNewsletterAutomationStep: NODE_TYPES.HTML_NEWSLETTER_STEP,
	AIEmailAutomationStep: NODE_TYPES.AI_EMAIL_STEP,
	SwitchAutomationStep: NODE_TYPES.SWITCH_STEP,
	BehaviorSwitchAutomationStep: NODE_TYPES.SWITCH_STEP,
	TriggerAutomationStep: NODE_TYPES.TRIGGER_AUTOMATION_STEP,
};

function getNodeTypeForStep(stepType: string): string {
	const result = STEP_TYPE_TO_NODE_TYPE[stepType] || NODE_TYPES.EMAIL_STEP;
	return result;
}

export function getTagStepDisplayName(step: Api.IAutomationStep): string | null {
	if (step._type !== 'AddTagAutomationStep' && step._type !== 'RemoveTagAutomationStep') {
		return null;
	}

	const tagStep = step as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep;
	const tagName = (tagStep.tagName || '').trim();
	const actionPrefix = step._type === 'AddTagAutomationStep' ? 'Add Tag' : 'Remove Tag';
	return tagName ? `${actionPrefix}: ${tagName}` : actionPrefix;
}

export function getActionItemTitle(step: Api.IAutomationStep): string {
	if (step._type !== 'ActionItemAutomationStep') {
		return '';
	}

	const actionItemStep = step as Api.IActionItemAutomationStep;
	if (actionItemStep.content) {
		try {
			const htmlContent = Api.getContentHtmlStringValueFromRawRichTextContentState(actionItemStep.content)
				.replace(/<[^>]+>/g, ' ')
				.replace(/&nbsp;/gi, ' ')
				.replace(/\s+/g, ' ')
				.trim();
			if (htmlContent.length > 0) {
				return htmlContent.substring(0, 100).trim();
			}
		} catch {
			// Fall back to rich-content plain text preview extraction below.
		}

		const { text } = getPlainTextPreviewForRawRichTextContent(actionItemStep.content, { maxCharCount: 100 });
		if (text.trim().length > 0) {
			return text.trim();
		}
	}

	const explicitName = (actionItemStep.name || '').trim();
	return explicitName;
}

function getStepNodeName(step: Api.IAutomationStep): string {
	const tagStepDisplayName = getTagStepDisplayName(step);
	if (tagStepDisplayName) {
		return tagStepDisplayName;
	}

	if (step._type === 'ActionItemAutomationStep') {
		return getActionItemTitle(step);
	}

	const stepName = 'name' in step ? step.name : undefined;
	return stepName && typeof stepName === 'string' ? stepName : '';
}

function getStepLabel(step: Api.IAutomationStep, index: number): string {
	const tagStepDisplayName = getTagStepDisplayName(step);
	if (tagStepDisplayName) {
		return tagStepDisplayName;
	}

	if (step._type === 'ActionItemAutomationStep') {
		const actionItemTitle = getActionItemTitle(step);
		return actionItemTitle ? `Action Item: ${actionItemTitle}` : `Action Item ${index + 1}`;
	}

	const stepName = 'name' in step ? step.name : undefined;
	if (stepName && typeof stepName === 'string') {
		return stepName;
	}

	const stepTypeLabel = getStepTypeLabel(step._type);
	return `${stepTypeLabel} ${index + 1}`;
}

function getStepTypeLabel(stepType: string): string {
	switch (stepType) {
		case 'EmailAutomationStep':
			return 'Email';
		case 'TextingAutomationStep':
			return 'Text Message';
		case 'ActionItemAutomationStep':
			return 'Action Item';
		case 'AddTagAutomationStep':
			return 'Add Tag';
		case 'RemoveTagAutomationStep':
			return 'Remove Tag';
		case 'HandwrittenCardAutomationStep':
			return 'Handwritten Card';
		case 'NoActionAutomationStep':
			return 'Do nothing';
		case 'OpportunityAutomationStep':
			return 'Opportunity';
		case 'HtmlNewsletterAutomationStep':
			return 'HTML Newsletter';
		case 'AIEmailAutomationStep':
			return 'AI Email';
		case 'SwitchAutomationStep':
			return 'Conditional';
		case 'BehaviorSwitchAutomationStep':
			return 'Behavior Branch';
		case 'TriggerAutomationStep':
			return 'Start Automation';
		default:
			return 'Step';
	}
}

/**
 * Converts a trigger's string _type to the AutomationTriggerType enum value.
 * This is needed because AutomationTriggerIcon expects enum values, not strings.
 * Since the enum values are the same strings, we can directly map them.
 */
export function normalizeTriggerType(trigger: Api.IAutomationTrigger): Api.IAutomationTrigger {
	if (!trigger || !trigger._type) {
		return trigger;
	}

	const triggerType = trigger._type as string;
	let normalizedType: Api.AutomationTriggerType | string | undefined;

	// Map string types to enum values (enum values are the same strings)
	switch (triggerType) {
		case 'NewClientAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewClient;
			break;
		case 'NewLeadAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewLead;
			break;
		case 'TagAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Tag;
			break;
		case 'AutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Meeting;
			break;
		case 'EventRegistrationTrigger':
			normalizedType = Api.AutomationTriggerType.EventRegistration;
			break;
		case 'ResourceSelectorAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.ResourceSelector;
			break;
		case 'TextingCampaignAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Texting;
			break;
		case 'ManualAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Manual;
			break;
		case 'NewDonorAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewDonor;
			break;
		case 'NewDonationTrigger':
			normalizedType = Api.AutomationTriggerType.NewDonationTrigger;
			break;
		default:
			// Return as-is if no mapping found
			return trigger;
	}

	// Create a new trigger object with the normalized type
	// AutomationTriggerType enum values are string literals that match the expected _type values
	return {
		...trigger,
		_type: normalizedType as Api.IAutomationTrigger['_type'],
	} as Api.IAutomationTrigger;
}

export function getTriggerLabel(trigger: Api.IAutomationTrigger): string {
	const triggerType = trigger._type as Api.AutomationTriggerType;

	// Tag trigger includes the tag name
	if (triggerType === Api.AutomationTriggerType.Tag) {
		return `Tag: ${(trigger as Api.ITagAutomationTrigger).tag || 'Applied'}`;
	}

	// Resource selector triggers use the resource selector label
	if (triggerType === Api.AutomationTriggerType.ResourceSelector) {
		const resourceTrigger = trigger as Api.IResourceSelectorAutomationTrigger;
		if (!resourceTrigger.resourceSelector) {
			return 'Custom Date';
		}
		return getResourceSelectorDisplayLabel(resourceTrigger.resourceSelector);
	}

	return getTriggerTypeLabel(triggerType);
}

function hasNonEmptyString(value: unknown): boolean {
	return typeof value === 'string' && value.trim().length > 0;
}

function isRichTextContentEmpty(content: unknown): boolean {
	if (!content) {
		return true;
	}

	// Some legacy/new-step paths may store content as an HTML string.
	if (typeof content === 'string') {
		const trimmed = content.trim();
		return trimmed === '' || trimmed === '<p></p>' || trimmed === '<p><br></p>';
	}

	// Current API shape: { document: '<p>...</p>' }
	if (typeof content === 'object' && content !== null) {
		const doc = (content as { document?: unknown }).document;
		if (typeof doc === 'string') {
			const trimmedDocument = doc.trim();
			return trimmedDocument === '' || trimmedDocument === '<p></p>' || trimmedDocument === '<p><br></p>';
		}
	}

	return false;
}

export function validateStep(step: Api.IAutomationStep): boolean {
	// Most steps require a schedule for publish-time validation.
	// Action items inherit Schedule from AutomationStep, but the API allows it to be omitted to represent "No due date".
	// Both switch types (regular SwitchAutomationStep and BehaviorSwitchAutomationStep) have their
	// Schedule computed by the API publisher at publish time, so the editor never supplies one.
	const hasSchedule = Boolean((step as { schedule?: unknown }).schedule);
	const hasValidSchedule = !doesAutomationStepRequireSchedule(step._type) || hasSchedule;

	let isStepConfigured = false;

	switch (step._type as string) {
		case 'EmailAutomationStep': {
			const emailStep = step as Api.IEmailAutomationStep;
			const subject = emailStep.options?.subject ?? emailStep.subject;
			isStepConfigured =
				hasNonEmptyString(emailStep.name) && hasNonEmptyString(subject) && !isRichTextContentEmpty(emailStep.content);
			break;
		}
		case 'HtmlNewsletterAutomationStep': {
			const newsletterStep = step as Api.IHtmlNewsletterAutomationStep;
			const subject = newsletterStep.options?.subject ?? newsletterStep.subject;
			isStepConfigured =
				hasNonEmptyString(newsletterStep.name) &&
				hasNonEmptyString(subject) &&
				!isRichTextContentEmpty(newsletterStep.content);
			break;
		}
		case 'AIEmailAutomationStep': {
			// Subject and body are written at send time, so the prompt is the only content the author supplies.
			const aiEmailStep = step as Api.IAIEmailAutomationStep;
			isStepConfigured = hasNonEmptyString(aiEmailStep.name) && hasNonEmptyString(aiEmailStep.prompt);
			break;
		}
		case 'TextingAutomationStep': {
			const textingStep = step as Api.ITextingAutomationStep;
			isStepConfigured = !isRichTextContentEmpty(textingStep.content);
			break;
		}
		case 'ActionItemAutomationStep': {
			const actionItemStep = step as Api.IActionItemAutomationStep;
			isStepConfigured = !isRichTextContentEmpty(actionItemStep.content);
			break;
		}
		case 'AddTagAutomationStep':
		case 'RemoveTagAutomationStep': {
			const tagStep = step as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep;
			isStepConfigured = hasNonEmptyString(tagStep.tagName);
			break;
		}
		case 'HandwrittenCardAutomationStep': {
			const hwcStep = step as Api.HandwrittenCardAutomationStep;
			isStepConfigured = Boolean(hwcStep.templateReference) && !isRichTextContentEmpty(hwcStep.content);
			break;
		}
		case 'SwitchAutomationStep': {
			const switchStep = step as Api.ISwitchAutomationStep;
			const cases = switchStep.cases || [];
			// Switch steps are minimally valid if they have at least one case + a default case.
			// More advanced validations (filters, empty segments) are handled by publish-time validation.
			isStepConfigured = cases.length > 0 && cases.some(c => c.isDefault);
			break;
		}
		case 'BehaviorSwitchAutomationStep': {
			const behaviorSwitchStep = step as Api.IAutomationStep & {
				waitTimeout?: unknown;
				cases?: BehaviorSwitchCaseLike[];
			};
			const { timeoutCaseCount, behaviorCaseCount } = getBehaviorSwitchCaseCounts(behaviorSwitchStep.cases);
			isStepConfigured =
				timeoutCaseCount === 1 && behaviorCaseCount > 0 && hasPositiveTimeSpan(behaviorSwitchStep.waitTimeout);
			break;
		}
		case 'TriggerAutomationStep': {
			const triggerStep = step as Api.IAutomationStep & { targetAutomationTemplateId?: string };
			isStepConfigured = hasNonEmptyString(triggerStep.targetAutomationTemplateId);
			break;
		}
		case 'OpportunityAutomationStep':
		case 'NoActionAutomationStep': {
			isStepConfigured = true;
			break;
		}
		default: {
			isStepConfigured = false;
			break;
		}
	}

	return hasValidSchedule && isStepConfigured;
}

function checkStepHasContent(step: Api.IAutomationStep): boolean {
	switch (step._type as string) {
		case 'EmailAutomationStep': {
			const emailStep = step as Api.IEmailAutomationStep;
			const subject = emailStep.options?.subject ?? emailStep.subject;
			return hasNonEmptyString(subject) || !isRichTextContentEmpty(emailStep.content);
		}
		case 'HtmlNewsletterAutomationStep': {
			const newsletterStep = step as Api.IHtmlNewsletterAutomationStep;
			const subject = newsletterStep.options?.subject ?? newsletterStep.subject;
			return hasNonEmptyString(subject) || !isRichTextContentEmpty(newsletterStep.content);
		}
		case 'AIEmailAutomationStep': {
			return hasNonEmptyString((step as Api.IAIEmailAutomationStep).prompt);
		}
		case 'TextingAutomationStep': {
			const textingStep = step as Api.ITextingAutomationStep;
			return !isRichTextContentEmpty(textingStep.content);
		}
		case 'ActionItemAutomationStep': {
			const actionItemStep = step as Api.IActionItemAutomationStep;
			return !isRichTextContentEmpty(actionItemStep.content);
		}
		case 'HandwrittenCardAutomationStep': {
			const hwcStep = step as Api.HandwrittenCardAutomationStep;
			return !isRichTextContentEmpty(hwcStep.content);
		}
		case 'TriggerAutomationStep': {
			const triggerStep = step as Api.IAutomationStep & { targetAutomationTemplateId?: string };
			return hasNonEmptyString(triggerStep.targetAutomationTemplateId);
		}
		default:
			return true;
	}
}
